import { Avatar, Button } from '@foundation/ui'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import styles from './AppLayout.module.css'
import type { ReactNode } from 'react'
import { clearAuthState, getAuthState } from '@/lib/auth-store'

interface AppLayoutProps {
  children: ReactNode
  title?: string
  action?: ReactNode
}

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: '📊' },
  { path: '/urls', label: 'Tracked URLs', icon: '🔗' },
  { path: '/alerts', label: 'Alerts', icon: '🔔' },
  { path: '/settings', label: 'Settings', icon: '⚙️' },
]

export function AppLayout({ children, title, action }: AppLayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const auth = getAuthState()

  const handleLogout = () => {
    clearAuthState()
    void navigate({ to: '/login' })
  }

  return (
    <div className={styles.layout}>
      {/* Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <Link to="/" className={styles.logo}>
            <span className={styles.logoIcon}>🔍</span>
            <span className={styles.logoText}>PriceSpy</span>
          </Link>
        </div>

        <nav className={styles.nav}>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`${styles.navItem} ${location.pathname.startsWith(item.path) ? styles.navItemActive : ''}`}
            >
              <span className={styles.navIcon}>{item.icon}</span>
              <span className={styles.navLabel}>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className={styles.sidebarFooter} />
      </aside>

      {/* Main content */}
      <div className={styles.main}>
        {/* Top header */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            {title && <h1 className={styles.pageTitle}>{title}</h1>}
          </div>
          <div className={styles.headerRight}>
            {action}
            <div className={styles.userMenu}>
              <Avatar name={auth.user?.name ?? auth.user?.email ?? 'U'} size="sm" />
              <span className={styles.userName}>{auth.user?.email}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className={styles.content}>{children}</main>
      </div>
    </div>
  )
}
